import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Permission } from '@core/enums/permissions';
import { RatingViewVersionEnum } from '@core/enums/rating-view-version.enum';
import { States } from '@core/enums/states';
import { UploadTypes } from '@core/enums/upload-types';
import { IRatingPeriod, ISchoolYear } from '@core/interfaces/iratingperiod';
import { AuthService } from '@core/services/auth.service';
import { DownloadTemplateService } from '@core/services/downloadTemplate.service';
import { LookupService } from '@core/services/lookup.service';
import { PermissionService } from '@core/services/permission.service';
import { StateService } from '@core/services/state.service';
import { ToastService } from '@core/services/toast.service';
import { TranslocoService } from '@jsverse/transloco';
import { take, tap } from 'rxjs';
import { take as takeOperator } from 'rxjs/operators';

@Component({
    selector: 'drdp-upload-templates',
    templateUrl: './upload-templates.component.html',
    styleUrls: ['./upload-templates.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UploadTemplatesComponent implements OnInit {
  stateId?: number;
  stateCode = '';
  enforceStateConfig?: boolean;
  currentSchoolYear?: ISchoolYear;
  overrideRatings = this.permissionService.checkPermission(Permission.OverrideLockedRatings);
  public get uploadTypes() { return UploadTypes }
  public get states() { return States; }
  constructor(
    private downloadTemplateService: DownloadTemplateService,
    private authService: AuthService,
    private toast: ToastService,
    public dialog: MatDialog,
    private stateService: StateService,
    private lookupService: LookupService,
    private permissionService: PermissionService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.stateId = this.authService.getUserStateId();
    this.stateCode = this.authService.getUserStateCode();
    this.getCurrentSchoolYear();
  }

  downloadTemplate(uploadType: number) {
    let fileURL = '';
    if (this.stateId) {
      this.downloadTemplateService.downloadTemplate(this.stateId, uploadType, { responseType: 'blob', observe: 'response' }).pipe(
        take(1),
        tap((res: any) => {
          if (res) {
            let fileName = this.getFileName(uploadType);            
            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(res.body);
            link.download = fileName;
            link.click();
          } else {
            this.toast.error(this.translocoService.translate('resource.uploadTemplates.templateUnavailableToast'))
          }
        })
      ).subscribe();
    }
  }

  getCurrentSchoolYear() {
    this.lookupService.getCurrentSchoolYear().pipe(
      take(1)
    ).subscribe((sy) => {
      this.currentSchoolYear = sy;
      this.checkStateConfiguration();
    });
  }

  checkStateConfiguration(): void {
    if (this.stateId && this.currentSchoolYear) {
      this.stateService.getStatesById(this.stateId!).pipe(
        take(1)
      ).subscribe((state: any) => {
        const ratingViewVersion = state.ratingViewVersionId ? state.ratingViewVersionId : RatingViewVersionEnum.VERSION_2015;
        this.enforceStateConfig = this.currentSchoolYear!.enforceStateConfig && (ratingViewVersion === RatingViewVersionEnum.VERSION_2025);
      });
    }
  }

  getFileName(uploadType: number): string {
    if (uploadType === this.uploadTypes.user) return `User-Upload-${this.stateCode}.xlsx`;
    if (uploadType === this.uploadTypes.class) return `Class-Upload-${this.stateCode}.xlsx`;
    if (uploadType === this.uploadTypes.student) return `All-Instrument-Upload-${this.stateCode}.xlsx`;
    if (uploadType === this.uploadTypes.student2025) return `All-Instrument-Upload-2025-${this.stateCode}.xlsx`;
    if (uploadType === this.uploadTypes.student2025Final) return `All-Instrument-Upload-${this.stateCode}-DRDP2025_V4.xlsx`;
    return 'Upload.xlsx'
  }
}
